'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useIsAppControl } from '@store/isApp'
import getNavigationType from '@utils/getNavigationType'
import type { ReactNode } from 'react'

interface ArticleTaboolaWrapperProps {
	children: ReactNode
}

const getTaboolaContents = (isApp?: boolean) => {
	if (isApp) {
		// feedC
		window._taboola.push({
			mode: 'thumbs-1x1',
			container: 'taboola-recommendation-c_app_1x4',
			placement: 'Recommendation C_App_1x4',
			target_type: 'mix',
		})
		// issue
		window._taboola.push({
			mode: 'thumbnails-a',
			container: 'taboola-hot-issue_app_stream_1x1',
			placement: 'Hot Issue_App_Stream_1x1',
			target_type: 'mix',
		})
		return
	}

	// feedA
	window._taboola.push({
		mode: 'thumbs-hero-01-n',
		container: 'taboola-recommendation-a_desktop_hero',
		placement: 'Recommendation A_Desktop_Hero',
		target_type: 'mix',
	})
	// feedB
	window._taboola.push({
		mode: 'alternating-thumbnails-2x3',
		container: 'taboola-recommendation-b_desktop_2x3',
		placement: 'Recommendation B_Desktop_2x3',
		target_type: 'mix',
	})
	// feedC
	window._taboola.push({
		mode: 'alternating-thumbnails-1x1',
		container: 'taboola-recommendation-c_mobile_1x4',
		placement: 'Recommendation C_Mobile_1x4',
		target_type: 'mix',
	})
	// scroll
	window._taboola.push({
		mode: 'alternating-thumbnails-2x1-H',
		container: 'taboola-infinite-feed_responsive',
		placement: 'Infinite Feed_Responsive',
		target_type: 'mix',
	})
}

const ArticleTaboolaWrapper = ({ children }: ArticleTaboolaWrapperProps) => {
	const ref = useRef<HTMLDivElement>(null)
	const timeoutRef = useRef<NodeJS.Timeout | undefined>()
	const { isApp } = useIsAppControl()
	const pathname = usePathname()

	/** Taboola 초기 셋팅 */
	useEffect(() => {
		if (!window) {
			return
		}

		window._taboola = window._taboola || []
		window._taboola.push({
			article: 'auto',
			url: window.location.href,
		})

		/**
		 * [ Taboola 의 newPageLoad 호출 시점 ]
		 * 전 페이지에서 광과를 호출하지 않았다면 initialPage 로 간주 (newPageLoad 실행)
		 *
		 * ex) A, B : 광고 있는 페이지 / C : 광고 없는 페이지
		 * - A -> B 이면 initialPage 가 아니므로 newPageLoad 실행 X (FIXME: 현재 이슈로 실행하도록 변경)
		 * - B -> C -> A 이면 결국 A 는 initialPage 이므로 newPageLoad 실행 O
		 * - 새로고침은 initialPage 아니므로 newPageLoad 실행 X (FIXME: 현재 이슈로 실행하도록 변경)
		 * - direct url 진입은 initialPage 이므로 newPageLoad 실행 O
		 */
		window._taboola.push({ notify: 'newPageLoad' })
	}, [pathname])

	/** 렌더링 만료된 시점에 flush 호출 */
	useEffect(() => {
		const observer = new MutationObserver(() => {
			getTaboolaContents(isApp)

			timeoutRef.current = setTimeout(() => {
				window._taboola.push({ flush: true })
			}, 1000)

			observer.disconnect()
		})

		if (ref.current) {
			observer.observe(ref.current, {
				childList: true,
				subtree: true,
			})
		}

		return () => {
			observer.disconnect()
			clearTimeout(timeoutRef.current)
		}
	}, [pathname, isApp])

	return <div ref={ref}>{children}</div>
}
export default ArticleTaboolaWrapper
